/**
 * JWT parsing utilities for extracting tenant claims from Auth0 tokens
 *
 * Note: This performs client-side decoding only (no verification).
 * Token verification is handled server-side by the API.
 */

import type { ITenantClaim, IParsedTenantClaim } from '@escloud/types';
import { jwtDecode } from 'jwt-decode';

/**
 * Custom claims namespace used in Auth0 tokens
 */
export const TENANT_CLAIMS_NAMESPACE = 'https://cloud.therenva.com/tenants';

/**
 * Identity claim set on the access token by the Auth0 post-login action, which
 * re-runs on every refresh exchange — so a fresh token carries the current
 * verification state, not the one from sign-in.
 */
export const EMAIL_VERIFIED_CLAIM = 'https://cloud.therenva.com/email_verified';

/**
 * Structure of the decoded JWT with tenant claims
 */
interface DecodedTokenWithTenants {
    [TENANT_CLAIMS_NAMESPACE]?: Record<string, ITenantClaim>;
    [EMAIL_VERIFIED_CLAIM]?: unknown;
    sub?: string;
    exp?: number;
    iat?: number;
}

/**
 * Parse tenant claims from an Auth0 access token
 *
 * Extracts the tenant metadata from the custom claims namespace.
 * Returns an empty array if parsing fails or no claims exist.
 *
 * @param token - JWT access token from Auth0
 * @returns Array of parsed tenant claims with IDs
 *
 * @example
 * ```typescript
 * const token = await getAccessTokenSilently();
 * const tenants = parseTenantClaimsFromToken(token);
 * // [{ id: 'tenant_abc', name: 'Acme', region: 'eu', role: 'owner' }]
 * ```
 */
export function parseTenantClaimsFromToken(token: string): IParsedTenantClaim[] {
    try {
        const decoded = jwtDecode<DecodedTokenWithTenants>(token);
        const claims = decoded[TENANT_CLAIMS_NAMESPACE];

        if (!claims || typeof claims !== 'object') {
            return [];
        }

        return Object.entries(claims).map(([id, claim]) => ({
            id,
            name: claim.name,
            slug: claim.slug,
            region: claim.region,
            role: claim.role,
            joinedAt: claim.joinedAt,
        }));
    } catch (error) {
        console.error('Failed to parse tenant claims from token:', error);
        return [];
    }
}

/**
 * Read the verified-email claim from an Auth0 access token — three answers,
 * not two: `true` verified, `false` proven unverified, `undefined` the claim
 * is not there to read (ESCLOUD-286).
 *
 * Which value counts as verified mirrors the server's `isVerified`: only a
 * literal `true` or the string `"true"`, because some connections have
 * historically returned the string and truthiness would let `"false"` pass.
 *
 * Absence is where this deliberately parts company with the server. There,
 * absent must mean "not verified": the answer decides whether an address may
 * permanently anchor an identity, so it fails closed. Here the question is
 * only which screen to show, and absent means the token cannot answer at all —
 * the post-login action that mints the claim may simply not be deployed to
 * this tenant yet, in which case reading it as "unverified" sends every
 * already-verified user to a screen that waits for a claim that will never
 * arrive. A caller that cannot tell the two apart must not act, which is what
 * `useEmailVerificationState` exists to express. An undecodable token is the
 * same kind of non-answer.
 */
export function parseEmailVerifiedClaim(token: string): boolean | undefined {
    try {
        const decoded = jwtDecode<DecodedTokenWithTenants>(token);
        const value = decoded[EMAIL_VERIFIED_CLAIM];
        if (value === undefined || value === null) {
            return undefined;
        }
        return value === true || value === 'true';
    } catch (error) {
        console.error('Failed to parse email_verified claim from token:', error);
        return undefined;
    }
}

/**
 * Get the region for a specific tenant from token claims
 *
 * @param token - JWT access token from Auth0
 * @param tenantId - ID of the tenant to look up
 * @returns Region code for the tenant, or null if not found
 */
export function getTenantRegionFromToken(token: string, tenantId: string): string | null {
    const claims = parseTenantClaimsFromToken(token);
    const tenant = claims.find((t) => t.id === tenantId);
    return tenant?.region ?? null;
}

/**
 * Check if a tenant ID exists in the token claims
 *
 * Useful for validating that a user still has access to a stored tenant.
 *
 * @param token - JWT access token from Auth0
 * @param tenantId - ID of the tenant to check
 * @returns true if the tenant exists in the claims
 */
export function isTenantInToken(token: string, tenantId: string): boolean {
    const claims = parseTenantClaimsFromToken(token);
    return claims.some((t) => t.id === tenantId);
}
